import { clsx } from 'clsx';
import { useNavigate } from 'react-router';
import type { SelectProps } from '../Select';
import Select from '../Select';
import type { TabItemWithState } from './makeUseTabs';

export interface TabProps {
    tabs: TabItemWithState[];
    onChange?: (key: string) => void;
    activeTabClassName?: string;
    /**
     * Visual style for desktop tabs
     * - 'underline': current style with bottom border indicator
     * - 'pills': rounded pills like Tailwind UI "Tabs in pills"
     */
    variant?: 'underline' | 'pills';
    /** Optional extra classes for the nav container */
    className?: string;
    switchToSelectOnMobile?: boolean;
}

const Tab = ({
    tabs,
    onChange,
    activeTabClassName,
    variant = 'underline',
    className,
    switchToSelectOnMobile,
}: TabProps) => {
    const navigate = useNavigate();

    const onSelectChange = (val: SelectProps['value']) => {
        const find = tabs.find(tab => tab.key === val);
        if (find?.href) {
            navigate(find.href);
        } else if (onChange) {
            onChange(val as string);
        }
    };

    const resolvedActiveClassName =
        activeTabClassName ??
        (variant === 'pills'
            ? 'bg-neutral-200 text-neutral-800 font-semibold'
            : 'border-teal-600 text-teal-600 font-semibold');

    return (
        <>
            {switchToSelectOnMobile && (
                <div className="sm:hidden">
                    <Select
                        name="tab"
                        onChange={onSelectChange}
                        options={tabs?.map(i => ({ value: i.key, label: i.name }))}
                        value={tabs?.find(tab => tab.current)?.key}
                    />
                </div>
            )}
            <div className={clsx(switchToSelectOnMobile && 'hidden', 'min-w-0 sm:block')}>
                <div className="w-full max-w-full overflow-x-auto overscroll-x-contain">
                    <nav
                        aria-label="Tabs"
                        className={clsx(
                            variant === 'underline' ? 'inline-flex w-max min-w-full' : 'inline-flex w-max rounded-md',
                            className
                        )}
                    >
                        {tabs?.map(tab =>
                            tab.href ? (
                                <a
                                    key={tab.key}
                                    aria-current={tab.current ? 'page' : undefined}
                                    className={clsx(
                                        tab.current
                                            ? resolvedActiveClassName
                                            : variant === 'underline'
                                              ? 'text-muted-foreground border-neutral-300 hover:border-neutral-400 hover:text-neutral-900'
                                              : 'text-muted-foreground hover:bg-white/50 hover:text-neutral-800',
                                        variant === 'underline'
                                            ? 'border-b-2 px-3 py-4 font-semibold whitespace-nowrap'
                                            : 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium whitespace-nowrap'
                                    )}
                                    href={tab.href}
                                >
                                    {tab.icon}
                                    {tab.name}
                                </a>
                            ) : (
                                <button
                                    key={tab.key}
                                    className={clsx(
                                        tab.current
                                            ? resolvedActiveClassName
                                            : variant === 'underline'
                                              ? 'text-muted-foreground border-neutral-300 hover:border-neutral-400 hover:text-neutral-900'
                                              : 'text-muted-foreground font-semibold hover:bg-white/50 hover:text-neutral-800',
                                        variant === 'underline'
                                            ? 'border-b-2 px-3 py-4 font-semibold whitespace-nowrap'
                                            : 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium whitespace-nowrap'
                                    )}
                                    onClick={() => onChange && onChange(tab.key)}
                                    type="button"
                                >
                                    {tab.icon}
                                    {tab.name}
                                </button>
                            )
                        )}
                        {variant === 'underline' && (
                            <div className="hidden flex-1 border-b-2 border-neutral-300 sm:block"></div>
                        )}
                    </nav>
                </div>
            </div>
        </>
    );
};

export default Tab;
